import React, { useState } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ProductCard = ({ product, onAddToCart, onQuickView, onToggleWishlist, isInWishlist }) => {
  const [selectedVariant, setSelectedVariant] = useState(product?.variants?.[0] || null);

  const handleAddToCart = () => {
    // HARD BLOCK: if product is a bundle, redirect to details modal instead
    const isBundle = product?.is_bundle === true || product?.isBundle === true;
    if (isBundle) {
      onQuickView?.(product);
      return;
    }
    onAddToCart(product);
  };

  // Determine stock status and styling
  const isLowStock = product?.trackInventory && product?.stockQuantity > 0 && product?.stockQuantity <= (product?.lowStockThreshold || 10);
  const isOutOfStock = product?.trackInventory && product?.stockQuantity === 0;

  // ── DISCOUNT LOGIC ──────────────────────────────────────────────────────
  const isBundle = product?.is_bundle === true || product?.isBundle === true;
  const bundleDiscount = parseFloat(product?.bundleDiscountPercentage ?? product?.bundle_discount_percentage ?? 0);

  // Dynamically calculate bundle price from child items (override hardcoded DB price)
  let displayPrice;
  let originalPrice;
  let hasDiscount;
  let discountPercentage;

  if (isBundle && product?.bundle_items && product?.bundle_items?.length > 0) {
    const childSum = product?.bundle_items?.reduce((sum, item) => sum + Number(item?.price || 0), 0);
    originalPrice = childSum;
    displayPrice = bundleDiscount > 0 ? childSum * (1 - bundleDiscount / 100) : childSum;
    hasDiscount = bundleDiscount > 0;
    discountPercentage = bundleDiscount;
  } else if (isBundle && product?.bundleItems && product?.bundleItems?.length > 0) {
    const childSum = product?.bundleItems?.reduce((sum, item) => sum + Number(item?.price || 0), 0);
    originalPrice = childSum;
    displayPrice = bundleDiscount > 0 ? childSum * (1 - bundleDiscount / 100) : childSum;
    hasDiscount = bundleDiscount > 0;
    discountPercentage = bundleDiscount;
  } else {
    // For regular products: hasDiscount if compareAtPrice > price
    hasDiscount = !!(product?.compareAtPrice && Number(product?.compareAtPrice) > Number(product?.price));
    displayPrice = Number(product?.price || 0);
    originalPrice = hasDiscount ? product?.compareAtPrice : null;
    discountPercentage = hasDiscount
      ? Math.round((1 - Number(product?.price) / Number(product?.compareAtPrice)) * 100)
      : 0;
  }
  // ────────────────────────────────────────────────────────────────────────

  return (
    <div className="group relative bg-card rounded-2xl border border-border overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
      {/* Discount Badge on image corner */}
      {hasDiscount && (
        <span className="absolute top-2 left-2 z-20 bg-emerald-500 text-white text-xs font-bold px-2 py-1 rounded shadow-md">
          %{discountPercentage} İndirim
        </span>
      )}
      {/* Legacy badge (non-discount) */}
      {product?.badge && !hasDiscount && (
        <div className="absolute top-4 left-4 z-10 px-3 py-1.5 rounded-full bg-error text-error-foreground text-xs font-bold uppercase tracking-wide shadow-lg">
          {product?.badge}
        </div>
      )}
      {/* Low Stock Badge */}
      {isLowStock && (
        <div className="absolute top-4 right-14 z-10 px-3 py-1.5 rounded-full bg-warning text-warning-foreground text-xs font-bold uppercase tracking-wide shadow-lg">
          Az Stok
        </div>
      )}
      {/* Out of Stock Overlay */}
      {isOutOfStock && (
        <div className="absolute inset-0 bg-black/60 z-10 flex items-center justify-center">
          <div className="bg-error text-error-foreground px-6 py-3 rounded-lg font-bold text-lg">
            Tükendi
          </div>
        </div>
      )}
      {/* Wishlist Button */}
      <button
        onClick={() => onToggleWishlist?.(product?.id)}
        className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-card/90 backdrop-blur-sm border border-border flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-all duration-200 shadow-lg"
        aria-label={isInWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
      >
        <Icon
          name={isInWishlist ? 'Heart' : 'Heart'}
          size={18}
          className={isInWishlist ? 'fill-current text-error' : ''}
        />
      </button>
      {/* Product Image */}
      <div
        onClick={() => onQuickView?.(product)}
        className="relative h-64 bg-white overflow-hidden cursor-pointer"
      >
        {product?.primaryImage || product?.image ? (
          <img
            src={product?.primaryImage || product?.image}
            alt={product?.imageUrls?.[0]?.alt || product?.title || 'Product image'}
            className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Icon name="Package" size={48} color="var(--color-muted-foreground)" />
          </div>
        )}
      </div>
      {/* Product Details */}
      <div className="p-5">
        {/* Brand & Category */}
        <div className="flex items-center justify-between mb-2">
          {product?.brand && (
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              {product?.brand}
            </span>
          )}
          {product?.category && (
            <span className="text-xs font-medium text-primary uppercase tracking-wide">
              {product?.category}
            </span>
          )}
        </div>

        {/* Title */}
        <h3 className="text-base font-semibold text-foreground mb-2 line-clamp-2 min-h-[48px]">
          {product?.title || 'Untitled Product'}
        </h3>

        {/* Description */}
        {product?.description && (
          <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
            {product?.description}
          </p>
        )}

        {/* Price Section — compare-at pricing */}
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          {hasDiscount ? (
            <>
              <span className="line-through text-gray-400 mr-1 text-sm">
                {Number(originalPrice)?.toLocaleString('tr-TR', { maximumFractionDigits: 2 })}₺
              </span>
              <span className="text-emerald-600 font-bold text-xl">
                {Number(displayPrice)?.toLocaleString('tr-TR', { maximumFractionDigits: 2 })}₺
              </span>
            </>
          ) : (
            <span className="text-2xl font-bold text-foreground">
              {Number(displayPrice)?.toLocaleString('tr-TR', { maximumFractionDigits: 2 })}₺
            </span>
          )}
        </div>

        {/* Stock Status */}
        <div className="flex items-center gap-2 mb-4">
          {product?.inStock ? (
            <>
              <div className={`w-2 h-2 rounded-full ${
                isLowStock ? 'bg-warning' : 'bg-success'
              }`}></div>
              <span className={`text-xs font-medium ${
                isLowStock ? 'text-warning' : 'text-success'
              }`}>
                {isLowStock
                  ? `Sadece ${product?.stockQuantity} adet kaldı!`
                  : product?.stockQuantity > 10
                  ? 'Stokta'
                  : `${product?.stockQuantity} adet kaldı`}
              </span>
            </>
          ) : (
            <>
              <div className="w-2 h-2 rounded-full bg-error"></div>
              <span className="text-xs text-error font-medium">Stokta Yok</span>
            </>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          <button
            onClick={() => onQuickView?.(product)}
            className="flex-1 px-4 py-2.5 rounded-lg border border-border bg-card hover:bg-muted text-foreground text-sm font-medium transition-colors flex items-center justify-center gap-2"
          >
            <Icon name="Eye" size={16} />
            <span>Görüntüle</span>
          </button>
          {(() => {
            const hasDirectVariants = product?.variants && product?.variants?.length > 0;
            const bundleItems = product?.bundle_items || product?.bundleItems;
            const bundleRequiresSelection = isBundle && bundleItems &&
              bundleItems?.some(item => item?.variants && item?.variants?.length > 0);
            const requiresOptions = hasDirectVariants || bundleRequiresSelection || isBundle;

            return requiresOptions ? (
              <button
                onClick={(e) => { e?.preventDefault(); onQuickView?.(product); }}
                disabled={!product?.inStock}
                className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                  product?.inStock
                    ? 'bg-gray-600 hover:bg-gray-700 text-white' :'bg-muted text-muted-foreground cursor-not-allowed'
                }`}
              >
                <Icon name="Layers" size={16} />
                <span>
                  {!product?.inStock
                    ? 'Tükendi'
                    : isBundle
                    ? 'Paketi İncele' :'Seçenekleri Gör'}
                </span>
              </button>
            ) : (
              <button
                onClick={(e) => { e?.preventDefault(); onAddToCart?.({ ...product, price: displayPrice }); }}
                disabled={!product?.inStock}
                className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                  product?.inStock
                    ? 'bg-primary text-primary-foreground hover:opacity-90'
                    : 'bg-muted text-muted-foreground cursor-not-allowed'
                }`}
              >
                <Icon name="ShoppingCart" size={16} />
                <span>{product?.inStock ? 'Sepete Ekle' : 'Tükendi'}</span>
              </button>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

export default ProductCard;